import {
  canonicalJsonStringify,
  parseShiftEditorDraft,
  ROUTE_DRAFT_WIRE_WRITE_MODE,
  type LocationSnapshot,
  type RouteTemplate,
  type RouteVariant,
  type ShiftAggregate,
  type ShiftEditorDraft,
} from '@der-maschinfuehrer/contracts';
import { useEffect, useMemo, useRef, useState, type FormEvent, type RefObject } from 'react';

import { FormErrorSummary, SaveStatus, TextField } from '../../components/form-primitives';
import {
  ACTIVE_CATALOG_VERSION,
  AlphaShiftDraftValidationError,
  addDraftVia,
  bindDraftToRouteVariant,
  buildShiftDraftBundleMutation,
  confirmManualRoute,
  createCopiedShiftEditorDraft,
  createEmptyOpenShiftMutation,
  createNextShiftEditorDraft,
  createReversedShiftEditorDraft,
  createShiftEditorDraft,
  createTemplateShiftEditorDraft,
  duplicateDraftVia,
  endpointToLocationSnapshot,
  localCalendarDate,
  hasExpandedRouteDraftState,
  matchesPersistedHistoricalRouteSelection,
  moveDraftVia,
  removeDraftVia,
  reverseDraftDirection,
  routeVariantMatchesConfirmedDraft,
  selectManualDraftRoute,
  updateDraftEndpoint,
  updateDraftVia,
  validateShiftEditorDraftForCommit,
  type DraftEndpointName,
  type DraftEndpointUpdateValue,
} from '../../domain/alpha-shift';
import {
  applyRouteOffer,
  applyAutomaticRouteOffer,
  bindRouteOfferToKnownVariant,
  clearAutomaticRouteSelection,
  composeRouteOffers,
  manualRouteSelected,
  routeOfferFavoriteMutation,
  routeOfferMatchesDraft,
  routeVariantMatchesRouteOffer,
  type NetworkRouteAvailability,
  type RouteOffer,
  type RouteOfferComposition,
} from '../../domain/route-candidates';
import { collectSegmentWarnings } from '../../domain/segment-warnings';
import {
  ShiftDraftAutosaveController,
  StoredShiftDraftPayloadError,
  type EditorSaveCoordinator,
} from '../../hooks/shift-draft-autosave';
import { useShiftDraftAutosave } from '../../hooks/use-shift-draft-autosave';
import { CatalogClient, catalogDocumentToLocationSnapshot } from '../../catalog/catalog-client';
import { NetworkGraphClientError, NetworkRouteClient } from '../../network/network-route-client';
import {
  AppDraftConflictError,
  AppEntityConflictError,
  AppProductionRollbackCompatibilityError,
  AppShiftConflictError,
  type AppDraftVersion,
  type AppStorage,
} from '../../storage/app-storage';
import { BackupPanel, type BackupOperations } from '../backup';
import { CatalogReadinessPanel } from './CatalogReadinessPanel';
import { NetworkGraphReadinessPanel } from './NetworkGraphReadinessPanel';
import { DraftEndpointField } from './DraftEndpointField';
import { RecentLocationsPanel } from './RecentLocationsPanel';
import {
  RouteCandidatePicker,
  type RouteCandidatePickerItem,
  type RouteCandidatePickerNetworkAvailability,
} from './RouteCandidatePicker';
import { RouteTemplatesPanel } from './RouteTemplatesPanel';
import { SaveUndoNotice } from './SaveUndoNotice';
import { SavedSegmentCard } from './SavedSegmentCard';
import { SegmentAdditionalFields } from './SegmentAdditionalFields';
import { ShiftCloseForm } from './ShiftCloseForm';
import { ViaPointsEditor } from './ViaPointsEditor';

export interface AlphaExperienceProps {
  readonly storage: AppStorage;
  readonly saveCoordinator: EditorSaveCoordinator;
}

type AlphaView =
  | { readonly kind: 'today' }
  | { readonly kind: 'new-shift' }
  | { readonly kind: 'history' }
  | { readonly kind: 'backup' }
  | {
      readonly kind: 'detail';
      readonly shiftId: string;
      readonly successMessage: string | null;
    }
  | {
      readonly kind: 'editor';
      readonly shiftId: string;
      readonly initialDraft: ShiftEditorDraft | null;
      readonly focusDestination: boolean;
      readonly requestedTripSegmentId: string | null;
      readonly session: number;
    };

class RouteActionGenerationError extends Error {
  constructor() {
    super('route action completed against a stale draft generation');
    this.name = 'RouteActionGenerationError';
  }
}

interface EditorRequest {
  readonly shiftId: string;
  readonly initialDraft: ShiftEditorDraft | null;
  readonly focusDestination: boolean;
  readonly requestedTripSegmentId: string | null;
}

interface SegmentUndoToken {
  readonly expectedAggregateRevision: number;
  readonly expectedTargetSegmentRevision: number;
  readonly shiftId: string;
  readonly tripSegmentId: string;
}

const DRAFT_KEY_PREFIX = 'shift-editor:';

export function AlphaExperience({ storage, saveCoordinator }: AlphaExperienceProps) {
  const [view, setView] = useState<AlphaView>({ kind: 'today' });
  const [dataRevision, setDataRevision] = useState(0);
  const [storageReopenRequired, setStorageReopenRequired] = useState(false);
  const [undoToken, setUndoToken] = useState<SegmentUndoToken | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);
  const nextEditorSession = useRef(1);
  const catalogClient = useMemo(
    () => new CatalogClient(storage.localProfileId),
    [storage.localProfileId],
  );
  const networkRouteClient = useMemo(() => new NetworkRouteClient(), []);
  const backupOperations = useMemo<BackupOperations>(
    () => ({
      createJsonBackup: () => storage.createJsonBackup(__BUILD_METADATA__.backup_provenance),
      previewJsonRestore: (bytes) => storage.previewJsonRestore(bytes),
      restoreJsonBackup: async (input) => {
        const result = await storage.restoreJsonBackup(input);
        setDataRevision((revision) => revision + 1);
        if (result.reopen_required) {
          setStorageReopenRequired(true);
        }
        return result;
      },
      verifyCurrentJsonBackup: (bytes) => storage.verifyCurrentJsonBackup(bytes),
    }),
    [storage],
  );

  useEffect(() => {
    void networkRouteClient.prepare().catch(() => {
      // The editor exposes a truthful fail-closed state and retries on an exact query.
    });
    return () => {
      catalogClient.dispose();
      networkRouteClient.dispose();
    };
  }, [catalogClient, networkRouteClient]);

  const openEditor = (request: EditorRequest) => {
    setView({ ...request, kind: 'editor', session: nextEditorSession.current++ });
  };

  const handleCommitted = (
    aggregate: ShiftAggregate,
    nextDraft: ShiftEditorDraft | null,
    tripSegmentId: string,
  ) => {
    setDataRevision((revision) => revision + 1);
    if (nextDraft !== null) {
      setUndoToken(null);
      openEditor({
        shiftId: aggregate.shift.shift_id,
        initialDraft: nextDraft,
        focusDestination: true,
        requestedTripSegmentId: null,
      });
      return;
    }
    const target = aggregate.segments.find((segment) => segment.trip_segment_id === tripSegmentId);
    setUndoError(null);
    setUndoToken(
      target === undefined
        ? null
        : {
            expectedAggregateRevision: aggregate.aggregate_revision,
            expectedTargetSegmentRevision: target.metadata.revision,
            shiftId: aggregate.shift.shift_id,
            tripSegmentId,
          },
    );
    setView({
      kind: 'detail',
      shiftId: aggregate.shift.shift_id,
      successMessage: 'Úsek byl bezpečně uložený do lokálního profilu.',
    });
  };

  const undoLastSegmentSave = async () => {
    if (undoToken === null) return;
    setUndoing(true);
    setUndoError(null);
    try {
      const aggregate = await storage.undoLatestSegmentSave({
        shift_id: undoToken.shiftId,
        trip_segment_id: undoToken.tripSegmentId,
        expected_aggregate_revision: undoToken.expectedAggregateRevision,
        expected_target_segment_revision: undoToken.expectedTargetSegmentRevision,
      });
      setDataRevision((revision) => revision + 1);
      setUndoToken(null);
      setView({
        kind: 'detail',
        shiftId: aggregate.shift.shift_id,
        successMessage: 'Poslední uložení úseku bylo vráceno novou auditovatelnou revizí.',
      });
    } catch (error) {
      setUndoError(
        errorMessage(error, 'Poslední uložení už nelze bezpečně vrátit; data se mezitím změnila.'),
      );
    } finally {
      setUndoing(false);
    }
  };

  return (
    <section className="alpha-experience" aria-label="Deník železničních jízd">
      <div className="alpha-profile-status" role="status">
        <span className="status-indicator" aria-hidden="true" />
        <span>
          <strong>Místní offline režim</strong>
          <span> Místní data jsou připravená a zůstávají v tomto zařízení.</span>
        </span>
      </div>

      {undoToken !== null && view.kind !== 'editor' ? (
        <SaveUndoNotice
          error={undoError}
          message="Úsek byl uložený."
          undoing={undoing}
          onDismiss={() => {
            setUndoError(null);
            setUndoToken(null);
          }}
          onUndo={() => void undoLastSegmentSave()}
        />
      ) : null}

      <nav className="alpha-navigation" aria-label="Části deníku">
        <button
          type="button"
          className="navigation-action touch-target"
          disabled={view.kind === 'editor' || storageReopenRequired}
          aria-current={view.kind === 'today' || view.kind === 'new-shift' ? 'page' : undefined}
          onClick={() => setView({ kind: 'today' })}
        >
          <NavigationIcon name="today" />
          <span>Dnes</span>
        </button>
        <button
          type="button"
          className="navigation-action touch-target"
          disabled={view.kind === 'editor' || storageReopenRequired}
          aria-current={view.kind === 'history' || view.kind === 'detail' ? 'page' : undefined}
          onClick={() => setView({ kind: 'history' })}
        >
          <NavigationIcon name="history" />
          <span>Historie</span>
        </button>
        <button
          type="button"
          className="navigation-action touch-target"
          aria-label="Data a záloha"
          disabled={view.kind === 'editor' || storageReopenRequired}
          aria-current={view.kind === 'backup' ? 'page' : undefined}
          onClick={() => setView({ kind: 'backup' })}
        >
          <NavigationIcon name="backup" />
          <span>Záloha</span>
        </button>
      </nav>

      {view.kind === 'today' ? (
        <TodayView
          catalogClient={catalogClient}
          networkRouteClient={networkRouteClient}
          storage={storage}
          dataRevision={dataRevision}
          onAddSegment={(aggregate) =>
            openEditor({
              shiftId: aggregate.shift.shift_id,
              initialDraft: liveShiftSegments(aggregate).some(
                (segment) => segment.destination !== null,
              )
                ? createNextShiftEditorDraft({ aggregate, uuid: newUuid })
                : null,
              focusDestination: liveShiftSegments(aggregate).some(
                (segment) => segment.destination !== null,
              ),
              requestedTripSegmentId: null,
            })
          }
          onNewShift={() => setView({ kind: 'new-shift' })}
          onOpenShift={(shiftId) => setView({ kind: 'detail', shiftId, successMessage: null })}
        />
      ) : null}
      {view.kind === 'new-shift' ? (
        <NewShiftView
          storage={storage}
          onCancel={() => setView({ kind: 'today' })}
          onCreated={(aggregate) => {
            setDataRevision((revision) => revision + 1);
            openEditor({
              shiftId: aggregate.shift.shift_id,
              initialDraft: null,
              focusDestination: false,
              requestedTripSegmentId: null,
            });
          }}
        />
      ) : null}
      {view.kind === 'history' ? (
        <HistoryView
          storage={storage}
          dataRevision={dataRevision}
          onOpenShift={(shiftId) => setView({ kind: 'detail', shiftId, successMessage: null })}
        />
      ) : null}
      {view.kind === 'backup' ? <BackupPanel operations={backupOperations} /> : null}
      {view.kind === 'detail' ? (
        <ShiftDetailView
          key={`${view.shiftId}:${dataRevision}`}
          dataRevision={dataRevision}
          storage={storage}
          shiftId={view.shiftId}
          successMessage={view.successMessage}
          onBack={() => setView({ kind: 'history' })}
          onOpenEditor={(nextDraft, focusDestination, requestedTripSegmentId) =>
            openEditor({
              shiftId: view.shiftId,
              initialDraft: nextDraft,
              focusDestination,
              requestedTripSegmentId,
            })
          }
        />
      ) : null}
      {view.kind === 'editor' ? (
        <ShiftEditorLoader
          key={view.session}
          catalogClient={catalogClient}
          networkRouteClient={networkRouteClient}
          storage={storage}
          saveCoordinator={saveCoordinator}
          shiftId={view.shiftId}
          initialDraft={view.initialDraft}
          focusDestination={view.focusDestination}
          requestedTripSegmentId={view.requestedTripSegmentId}
          onCancel={() => setView({ kind: 'detail', shiftId: view.shiftId, successMessage: null })}
          onCommitted={handleCommitted}
          onReload={() => setView({ kind: 'detail', shiftId: view.shiftId, successMessage: null })}
        />
      ) : null}
    </section>
  );
}

function NavigationIcon({ name }: { readonly name: 'backup' | 'history' | 'today' }) {
  const path = {
    backup: 'M7 4h10l3 3v13H4V4h3Zm1 0v6h8V4M8 15h8',
    history: 'M5 5h14v15H5V5Zm4-2v4m6-4v4M5 10h14m-11 4h3m2 0h3',
    today: 'M4 10 12 4l8 6v10h-5v-6H9v6H4V10Z',
  }[name];
  return (
    <svg className="navigation-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d={path} />
    </svg>
  );
}

function TodayView({
  catalogClient,
  networkRouteClient,
  storage,
  dataRevision,
  onAddSegment,
  onNewShift,
  onOpenShift,
}: {
  readonly catalogClient: CatalogClient;
  readonly networkRouteClient: NetworkRouteClient;
  readonly storage: AppStorage;
  readonly dataRevision: number;
  readonly onAddSegment: (aggregate: ShiftAggregate) => void;
  readonly onNewShift: () => void;
  readonly onOpenShift: (shiftId: string) => void;
}) {
  const [visibleShifts, setVisibleShifts] = useState<readonly ShiftAggregate[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void storage.listShifts().then(
      (shifts) => {
        if (active) {
          const today = localCalendarDate();
          const relevant = shifts.filter(
            (shift) => shift.shift.status === 'open' || shift.shift.service_date === today,
          );
          setVisibleShifts(relevant.length > 0 ? relevant : shifts.slice(0, 1));
        }
      },
      (error: unknown) => {
        if (active) {
          setLoadError(errorMessage(error, 'Dnešní směny se nepodařilo načíst.'));
        }
      },
    );
    return () => {
      active = false;
    };
  }, [dataRevision, storage]);

  return (
    <div className="alpha-view alpha-today">
      <div className="screen-heading">
        <p className="eyebrow">Dnešní provoz</p>
        <h2>Dnes</h2>
        <p>Otevři směnu a zapisuj její úseky rychle i bez připojení.</p>
      </div>
      <button
        type="button"
        className="primary-action primary-action-large touch-target"
        onClick={onNewShift}
      >
        <span aria-hidden="true">＋</span>
        Nová směna
      </button>
      <CatalogReadinessPanel client={catalogClient} dataRevision={dataRevision} storage={storage} />
      <NetworkGraphReadinessPanel client={networkRouteClient} />
      <h3>Otevřené, dnešní a poslední směny</h3>
      {loadError ? <p role="alert">{loadError}</p> : null}
      {visibleShifts === null && loadError === null ? <p role="status">Načítám směny…</p> : null}
      {visibleShifts?.length === 0 ? (
        <div className="empty-state">
          <strong>Deník je připravený.</strong>
          <p>Zatím tu není žádná směna. Začni tlačítkem Nová směna.</p>
        </div>
      ) : null}
      {visibleShifts && visibleShifts.length > 0 ? (
        <ul className="shift-list">
          {visibleShifts.map((aggregate) => (
            <li key={aggregate.shift.shift_id}>
              <article className="shift-list-item">
                <h4>
                  {aggregate.shift.status === 'open' ? 'Otevřená' : 'Uzavřená'} směna{' '}
                  {aggregate.shift.service_date}
                </h4>
                <p>{segmentCount(aggregate)}</p>
                <button
                  type="button"
                  className="secondary-action touch-target"
                  onClick={() => onOpenShift(aggregate.shift.shift_id)}
                >
                  {aggregate.shift.status === 'open' && liveShiftSegments(aggregate).length > 0
                    ? `Pokračovat z ${liveShiftSegments(aggregate).at(-1)?.destination?.display_name ?? 'posledního úseku'}`
                    : `Otevřít směnu ${aggregate.shift.service_date}`}
                </button>
                {aggregate.shift.status === 'open' ? (
                  <button
                    type="button"
                    className="primary-action touch-target"
                    onClick={() => onAddSegment(aggregate)}
                  >
                    + Jízda
                  </button>
                ) : null}
              </article>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function NewShiftView({
  storage,
  onCancel,
  onCreated,
}: {
  readonly storage: AppStorage;
  readonly onCancel: () => void;
  readonly onCreated: (aggregate: ShiftAggregate) => void;
}) {
  const heading = useHeadingFocus();
  const [serviceDate, setServiceDate] = useState(() => localCalendarDate());
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<readonly string[]>([]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrors([]);
    setSaving(true);
    try {
      const aggregate = await storage.saveShift(
        createEmptyOpenShiftMutation({
          datasetEpoch: storage.datasetEpoch,
          shiftId: newUuid(),
          serviceDate,
        }),
        null,
      );
      onCreated(aggregate);
    } catch (error) {
      setErrors([errorMessage(error, 'Směnu se nepodařilo vytvořit.')]);
      setSaving(false);
    }
  };

  return (
    <div className="alpha-view alpha-new-shift">
      <h2 ref={heading} tabIndex={-1}>
        Nová směna
      </h2>
      <FormErrorSummary errors={errors} />
      <form aria-label="Nová směna" onSubmit={(event) => void handleSubmit(event)}>
        <TextField
          label="Datum služby"
          name="service-date"
          type="date"
          value={serviceDate}
          disabled={saving}
          required
          onChange={(event) => setServiceDate(event.currentTarget.value)}
        />
        <div className="form-actions">
          <button type="submit" className="primary-action touch-target" disabled={saving}>
            {saving ? 'Otevírám směnu…' : 'Otevřít směnu'}
          </button>
          <button
            type="button"
            className="secondary-action touch-target"
            disabled={saving}
            onClick={onCancel}
          >
            Zrušit
          </button>
        </div>
      </form>
    </div>
  );
}

interface LoadedEditorSession {
  readonly aggregate: ShiftAggregate;
  readonly controller: ShiftDraftAutosaveController;
  readonly draft: ShiftEditorDraft;
  readonly loadedStoredDraft: boolean;
}

interface LoadedRouteOfferData {
  readonly routeVariants: readonly RouteVariant[];
  readonly shifts: readonly ShiftAggregate[];
  readonly templates: readonly RouteTemplate[];
}

const INITIAL_NETWORK_AVAILABILITY: NetworkRouteAvailability = { status: 'loading' };

interface BlockedEditorState {
  readonly discardTarget: {
    readonly draftKey: string;
    readonly expectedVersion: AppDraftVersion;
  } | null;
  readonly message: string;
}

function ShiftEditorLoader({
  catalogClient,
  networkRouteClient,
  storage,
  saveCoordinator,
  shiftId,
  initialDraft,
  focusDestination,
  requestedTripSegmentId,
  onCancel,
  onCommitted,
  onReload,
}: {
  readonly catalogClient: CatalogClient;
  readonly networkRouteClient: NetworkRouteClient;
  readonly storage: AppStorage;
  readonly saveCoordinator: EditorSaveCoordinator;
  readonly shiftId: string;
  readonly initialDraft: ShiftEditorDraft | null;
  readonly focusDestination: boolean;
  readonly requestedTripSegmentId: string | null;
  readonly onCancel: () => void;
  readonly onCommitted: (
    aggregate: ShiftAggregate,
    nextDraft: ShiftEditorDraft | null,
    tripSegmentId: string,
  ) => void;
  readonly onReload: () => void;
}) {
  const [session, setSession] = useState<LoadedEditorSession | null>(null);
  const [blocked, setBlocked] = useState<BlockedEditorState | null>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [discardError, setDiscardError] = useState<string | null>(null);
  const discardConfirmation = useRef<HTMLDivElement>(null);
  const discardFailure = useRef<HTMLDivElement>(null);
  const heading = useHeadingFocus(blocked?.message);

  useEffect(() => {
    if (confirmingDiscard) {
      discardConfirmation.current?.focus();
    }
  }, [confirmingDiscard]);

  useEffect(() => {
    if (discardError !== null) {
      discardFailure.current?.focus();
    }
  }, [discardError]);

  useEffect(() => {
    let active = true;
    let controller: ShiftDraftAutosaveController | null = null;
    const load = async () => {
      let discardTarget: BlockedEditorState['discardTarget'] = null;
      try {
        const aggregate = await storage.getShift(shiftId);
        if (aggregate === null) {
          throw new Error('Směna už v lokálním profilu neexistuje.');
        }
        const draftKey = draftKeyFor(shiftId);
        const record = await storage.getDraft(draftKey);
        let draft: ShiftEditorDraft;
        if (record !== null) {
          discardTarget = safeDiscardTarget(record, draftKey, storage);
          if (record.draft_key !== draftKey || record.draft_kind !== 'shift_editor') {
            throw new StoredShiftDraftPayloadError('Uložený koncept nepatří tomuto editoru.');
          }
          draft = parseShiftEditorDraft(record.payload);
          assertDraftMatchesAggregate(draft, aggregate);
          assertDraftMatchesEditorRequest(draft, aggregate, requestedTripSegmentId);
          if (requestedTripSegmentId === null && initialDraft !== null) {
            assertDraftMatchesInitialRequest(draft, initialDraft);
          }
          controller = new ShiftDraftAutosaveController({
            storage,
            draftKey,
            initialRecord: record,
          });
        } else {
          draft =
            initialDraft ??
            createShiftEditorDraft({
              aggregate,
              uuid: newUuid,
            });
          assertDraftMatchesAggregate(draft, aggregate);
          assertDraftMatchesEditorRequest(draft, aggregate, requestedTripSegmentId);
          controller = new ShiftDraftAutosaveController({ storage, draftKey });
          controller.setDraft(draft);
        }
        if (active) {
          setSession({
            aggregate,
            controller,
            draft,
            loadedStoredDraft: record !== null,
          });
        } else {
          controller.dispose();
        }
      } catch (error) {
        controller?.dispose();
        if (active) {
          setBlocked({
            discardTarget,
            message: errorMessage(
              error,
              'Koncept se nepodařilo bezpečně otevřít. Zůstal beze změny v úložišti.',
            ),
          });
        }
      }
    };
    void load();
    return () => {
      active = false;
      controller?.dispose();
    };
  }, [initialDraft, requestedTripSegmentId, shiftId, storage]);

  const discardBlockedDraft = async () => {
    const target = blocked?.discardTarget;
    if (target === null || target === undefined || !confirmingDiscard || discarding) {
      return;
    }
    setDiscardError(null);
    setDiscarding(true);
    try {
      await storage.discardDraft(target.draftKey, target.expectedVersion);
      onReload();
    } catch (error) {
      if (error instanceof AppDraftConflictError) {
        setBlocked((current) =>
          current === null
            ? null
            : {
                ...current,
                discardTarget: null,
              },
        );
        setDiscardError(
          'Koncept mezitím změnila jiná relace. Novější koncept nebyl smazán. Vrať se na uloženou směnu a otevři editor znovu.',
        );
      } else {
        setDiscardError(
          errorMessage(
            error,
            'Koncept se nepodařilo zahodit. Zůstal beze změny v lokálním úložišti.',
          ),
        );
      }
      setConfirmingDiscard(false);
      setDiscarding(false);
    }
  };

  if (blocked !== null) {
    return (
      <div className="alpha-view alpha-editor-blocked">
        <h2 ref={heading} tabIndex={-1}>
          Koncept nelze bezpečně otevřít
        </h2>
        <div className="error-summary" role="alert">
          <strong>Zápis je zablokovaný.</strong>
          <p>{blocked.message}</p>
          <p>Uložený nebo zastaralý koncept nebyl smazán ani přepsán.</p>
        </div>
        {discardError !== null ? (
          <div ref={discardFailure} className="error-summary" role="alert" tabIndex={-1}>
            <strong>Koncept nebyl zahozen.</strong>
            <p>{discardError}</p>
          </div>
        ) : null}
        {blocked.discardTarget !== null && !confirmingDiscard ? (
          <button
            type="button"
            className="danger-action touch-target"
            disabled={discarding}
            onClick={() => {
              setDiscardError(null);
              setConfirmingDiscard(true);
            }}
          >
            Zahodit koncept
          </button>
        ) : null}
        {blocked.discardTarget !== null && confirmingDiscard ? (
          <div ref={discardConfirmation} className="draft-conflict" role="alert" tabIndex={-1}>
            <strong>Opravdu nevratně zahodit tento koncept?</strong>
            <p>
              Rozpracované údaje z tohoto konceptu už nepůjde obnovit. Uložená směna a její uložené
              úseky zůstanou zachované.
            </p>
            <div className="form-actions">
              <button
                type="button"
                className="danger-action touch-target"
                disabled={discarding}
                onClick={() => void discardBlockedDraft()}
              >
                {discarding ? 'Zahazuji koncept…' : 'Ano, nevratně zahodit koncept'}
              </button>
              <button
                type="button"
                className="secondary-action touch-target"
                disabled={discarding}
                onClick={() => setConfirmingDiscard(false)}
              >
                Nezahazovat koncept
              </button>
            </div>
          </div>
        ) : null}
        <button
          type="button"
          className="secondary-action touch-target"
          disabled={discarding}
          onClick={onCancel}
        >
          Zpět na uloženou směnu
        </button>
      </div>
    );
  }

  if (session === null) {
    return (
      <div className="alpha-view" role="status">
        Načítám směnu a lokální koncept…
      </div>
    );
  }

  return (
    <ShiftEditor
      aggregate={session.aggregate}
      catalogClient={catalogClient}
      networkRouteClient={networkRouteClient}
      controller={session.controller}
      initialDraft={session.draft}
      loadedStoredDraft={session.loadedStoredDraft}
      storage={storage}
      saveCoordinator={saveCoordinator}
      focusDestination={focusDestination}
      onCancel={onCancel}
      onCommitted={onCommitted}
      onReload={onReload}
    />
  );
}

function ShiftEditor({
  aggregate,
  catalogClient,
  networkRouteClient,
  controller,
  initialDraft,
  loadedStoredDraft,
  storage,
  saveCoordinator,
  focusDestination,
  onCancel,
  onCommitted,
  onReload,
}: {
  readonly aggregate: ShiftAggregate;
  readonly catalogClient: CatalogClient;
  readonly networkRouteClient: NetworkRouteClient;
  readonly controller: ShiftDraftAutosaveController;
  readonly initialDraft: ShiftEditorDraft;
  readonly loadedStoredDraft: boolean;
  readonly storage: AppStorage;
  readonly saveCoordinator: EditorSaveCoordinator;
  readonly focusDestination: boolean;
  readonly onCancel: () => void;
  readonly onCommitted: (
    aggregate: ShiftAggregate,
    nextDraft: ShiftEditorDraft | null,
    tripSegmentId: string,
  ) => void;
  readonly onReload: () => void;
}) {
  const heading = useHeadingFocus();
  const destinationInput = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(initialDraft);
  const [errors, setErrors] = useState<readonly string[]>([]);
  const [committing, setCommitting] = useState(false);
  const [confirmingRoute, setConfirmingRoute] = useState(false);
  const [confirmingDraftDiscard, setConfirmingDraftDiscard] = useState(false);
  const [discardingDraft, setDiscardingDraft] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [commitConflict, setCommitConflict] = useState<string | null>(null);
  const [resolutionBlocked, setResolutionBlocked] = useState<string | null>(null);
  const [failClosedBlock, setFailClosedBlock] = useState<string | null>(null);
  const [routeOfferData, setRouteOfferData] = useState<LoadedRouteOfferData | null>(null);
  const [routeOfferLoadError, setRouteOfferLoadError] = useState<string | null>(null);
  const [routeOfferActionError, setRouteOfferActionError] = useState<string | null>(null);
  const [networkAvailability, setNetworkAvailability] = useState<NetworkRouteAvailability>(
    INITIAL_NETWORK_AVAILABILITY,
  );
  const [favoriteBusyId, setFavoriteBusyId] = useState<string | null>(null);
  const [selectingOfferId, setSelectingOfferId] = useState<string | null>(null);
  const discardConfirmation = useRef<HTMLDivElement>(null);
  const failClosedError = useRef<HTMLDivElement>(null);
  const draftRef = useRef(initialDraft);
  const draftGeneration = useRef(0);
  const routeActionBusy = useRef(false);
  const favoriteActionBusy = useRef(false);
  const [routeEvaluatedAt] = useState(() => new Date().toISOString());
  const [durableConfirmationKey, setDurableConfirmationKey] = useState<string | null>(() =>
    loadedStoredDraft ? confirmationKey(initialDraft) : null,
  );
  const autosave = useShiftDraftAutosave(controller, { saveCoordinator });

  const routeOffers = useMemo<RouteOfferComposition | null>(() => {
    if (routeOfferData === null) return null;
    const currentShifts = routeOfferData.shifts.some(
      (shift) => shift.shift.shift_id === aggregate.shift.shift_id,
    )
      ? routeOfferData.shifts.map((shift) =>
          shift.shift.shift_id === aggregate.shift.shift_id ? aggregate : shift,
        )
      : [aggregate, ...routeOfferData.shifts];
    return composeRouteOffers({
      draft,
      evaluatedAt: routeEvaluatedAt,
      network: networkAvailability,
      routeVariants: routeOfferData.routeVariants,
      shifts: currentShifts,
      templates: routeOfferData.templates,
    });
  }, [aggregate, draft, networkAvailability, routeEvaluatedAt, routeOfferData]);
  const allRouteOffers = useMemo(
    () =>
      routeOffers === null
        ? []
        : [...routeOffers.history, ...routeOffers.templates, ...routeOffers.network],
    [routeOffers],
  );
  const selectedRouteOffer =
    allRouteOffers.find((offer) => routeOfferMatchesDraft(draft, offer)) ?? null;
  const pickerCandidates = useMemo(() => {
    if (routeOffers === null) return [];
    const mapOffer = (
      offer: RouteOffer,
      group: RouteCandidatePickerItem['group'],
    ): RouteCandidatePickerItem =>
      routeOfferToPickerItem(
        offer,
        group,
        selectedRouteOffer?.offer_id === offer.offer_id && draft.route_confirmation !== null,
        selectedRouteOffer?.offer_id === offer.offer_id && draft.route_selection?.kind === 'offer'
          ? draft.route_selection.selection_method
          : null,
      );
    return [
      ...routeOffers.history.map((offer) => mapOffer(offer, 'history')),
      ...routeOffers.templates.map((offer) => mapOffer(offer, 'templates')),
      ...routeOffers.network.map((offer) => mapOffer(offer, 'network')),
    ];
  }, [draft.route_confirmation, draft.route_selection, routeOffers, selectedRouteOffer]);

  useEffect(() => {
    if (focusDestination) {
      destinationInput.current?.focus();
    }
  }, [focusDestination]);

  useEffect(() => {
    if (confirmingDraftDiscard) {
      discardConfirmation.current?.focus();
    }
  }, [confirmingDraftDiscard]);

  useEffect(() => {
    if (failClosedBlock !== null) {
      failClosedError.current?.focus();
    }
  }, [failClosedBlock]);

  useEffect(() => {
    let active = true;
    void Promise.all([
      storage.listShifts(),
      storage.listRouteVariants({ include_deleted: true }),
      storage.listRouteTemplates(),
    ]).then(
      ([shifts, routeVariants, templates]) => {
        if (active) setRouteOfferData({ routeVariants, shifts, templates });
      },
      (error: unknown) => {
        if (active) {
          setRouteOfferLoadError(
            errorMessage(
              error,
              'Dříve použité trasy a šablony se nepodařilo načíst. Jinou trasu můžeš dál zvolit ručně.',
            ),
          );
        }
      },
    );
    return () => {
      active = false;
    };
  }, [storage]);

  useEffect(() => {
    let active = true;
    const destination = draft.destination;
    const originSnapshot = safeEndpointSnapshot(draft.origin);
    const destinationSnapshot = destination === null ? null : safeEndpointSnapshot(destination);
    void Promise.resolve().then(async () => {
      if (!active) return;
      if (destination === null) {
        setNetworkAvailability({ status: 'ready_no_route' });
        return;
      }
      setNetworkAvailability({ status: 'loading' });
      try {
        const availability = await networkRouteClient.query({
          origin: originSnapshot,
          originCatalogDocumentId: draft.origin.catalog_document_id,
          originCatalogVersion: draft.origin.catalog_version,
          destination: destinationSnapshot,
          destinationCatalogDocumentId: destination.catalog_document_id,
          destinationCatalogVersion: destination.catalog_version,
        });
        if (active) setNetworkAvailability(availability);
      } catch (error) {
        if (!active) return;
        setNetworkAvailability({
          status: 'unavailable',
          reason: error instanceof NetworkGraphClientError ? error.reason : 'corrupt',
        });
      }
    });
    return () => {
      active = false;
    };
  }, [draft.destination, draft.origin, networkRouteClient]);

  const interactionBlocked =
    committing ||
    confirmingRoute ||
    selectingOfferId !== null ||
    favoriteBusyId !== null ||
    discardingDraft ||
    leaving ||
    autosave.status === 'conflict' ||
    commitConflict !== null ||
    failClosedBlock !== null;
  const controlsDisabled = interactionBlocked || confirmingDraftDiscard;
  const validation = validateShiftEditorDraftForCommit(draft);
  const currentConfirmationKey = confirmationKey(draft);
  const persistedTarget = aggregate.segments.find(
    (segment) =>
      segment.trip_segment_id === draft.trip_segment_id && segment.metadata.tombstone === null,
  );
  const expandedNonPerformanceUnavailable =
    ROUTE_DRAFT_WIRE_WRITE_MODE === 'compatibility_reader' &&
    !Object.hasOwn(persistedTarget ?? {}, 'route_draft_snapshot') &&
    hasExpandedRouteDraftState(draft);
  const expandedNonPerformanceBlocked =
    expandedNonPerformanceUnavailable && (draft.status === 'draft' || draft.status === 'cancelled');
  const persistedRouteVariantId = persistedTarget?.route_snapshot?.route_variant_id ?? null;
  const routeMutationOptions = { persistedRouteVariantId };

  useEffect(() => {
    if (routeOffers === null || routeActionBusy.current) return;
    if (draft.route_confirmation !== null) return;
    const preselectedId = routeOffers?.preselected_offer_id ?? null;
    const selection = draft.route_selection;
    const mayUpdate =
      selection === null ||
      (selection.kind === 'offer' && selection.selection_method === 'automatic');
    if (!mayUpdate) return;
    const startingGeneration = draftGeneration.current;
    let active = true;
    void Promise.resolve().then(() => {
      if (
        !active ||
        routeActionBusy.current ||
        startingGeneration !== draftGeneration.current ||
        draftRef.current !== draft
      ) {
        return;
      }
      try {
        const offer =
          preselectedId === null
            ? undefined
            : allRouteOffers.find((candidate) => candidate.offer_id === preselectedId);
        const next =
          offer === undefined
            ? clearAutomaticRouteSelection(draft, newUuid)
            : routeOfferMatchesDraft(draft, offer) &&
                selection?.kind === 'offer' &&
                selection.selection_method === 'automatic'
              ? draft
              : applyAutomaticRouteOffer(draft, offer, newUuid);
        if (next === draft) return;
        draftRef.current = next;
        draftGeneration.current += 1;
        setErrors([]);
        setRouteOfferActionError(null);
        setDurableConfirmationKey(null);
        setDraft(next);
        controller.setDraft(next);
      } catch (error) {
        setRouteOfferActionError(
          errorMessage(
            error,
            'Automatickou předvolbu trasy se nepodařilo bezpečně aktualizovat. Vyber trasu výslovně.',
          ),
        );
      }
    });
    return () => {
      active = false;
    };
  }, [allRouteOffers, controller, draft, routeOffers]);

  const hasDurableConfirmation =
    currentConfirmationKey === null
      ? draft.route_confirmation === null &&
        (draft.status === 'draft' || draft.status === 'cancelled')
      : currentConfirmationKey === durableConfirmationKey;
  const commitDisabled =
    controlsDisabled ||
    !validation.valid ||
    !hasDurableConfirmation ||
    autosave.status === 'error' ||
    expandedNonPerformanceBlocked;

  const changeEndpoint = (endpointName: DraftEndpointName, value: DraftEndpointUpdateValue) => {
    if (routeActionBusy.current) return;
    setErrors([]);
    if (endpointName !== 'planned_destination') setDurableConfirmationKey(null);
    const next = updateDraftEndpoint(draft, endpointName, value, newUuid, routeMutationOptions);
    draftRef.current = next;
    draftGeneration.current += 1;
    setDraft(next);
    controller.setDraft(next);
  };

  const changeDraft = (next: ShiftEditorDraft, allowDuringRouteAction = false) => {
    if (routeActionBusy.current && !allowDuringRouteAction) return;
    setErrors([]);
    if (draft.route_confirmation !== null && next.route_confirmation === null) {
      setDurableConfirmationKey(null);
    }
    draftRef.current = next;
    draftGeneration.current += 1;
    setDraft(next);
    controller.setDraft(next);
  };

  const selectRouteOffer = async (offerId: string) => {
    if (routeActionBusy.current || favoriteActionBusy.current) return;
    const offer = allRouteOffers.find((candidate) => candidate.offer_id === offerId);
    if (offer === undefined) {
      setRouteOfferActionError('Zvolená trasa už není v aktuální nabídce. Vyber ji znovu.');
      return;
    }
    const startingDraft = draftRef.current;
    const startingGeneration = draftGeneration.current;
    routeActionBusy.current = true;
    setSelectingOfferId(offerId);
    try {
      setRouteOfferActionError(null);
      const nextDraft = applyRouteOffer(
        startingDraft,
        offer,
        newUuid,
        routeMutationOptions,
        'explicit',
      );
      if (offer.offer_source === 'template') {
        const template = routeOfferData?.templates.find(
          (candidate) => candidate.route_template_id === offer.route_template_id,
        );
        if (template === undefined || template.source_snapshot === undefined) {
          throw new Error('Vybraná šablona už nemá použitelnou uloženou podobu trasy.');
        }
        const applied = await controller.persistAtomicDraftMutation({
          expected_current_draft: startingDraft,
          next_draft: nextDraft,
          write: (draft) =>
            storage.applyRouteTemplateToDraft({
              draft: { ...draft, draft_kind: 'shift_editor' },
              template: {
                route_template_id: template.route_template_id,
                expected_revision: template.metadata.revision,
              },
            }),
        });
        const persistedDraft = parseShiftEditorDraft(applied.draft.payload);
        setRouteOfferData((current) =>
          current === null
            ? null
            : {
                ...current,
                templates: current.templates.map((candidate) =>
                  candidate.route_template_id === applied.route_template.route_template_id
                    ? applied.route_template
                    : candidate,
                ),
              },
        );
        setErrors([]);
        setDurableConfirmationKey(null);
        draftRef.current = persistedDraft;
        draftGeneration.current += 1;
        setDraft(persistedDraft);
        return;
      }
      if (startingGeneration !== draftGeneration.current || startingDraft !== draftRef.current) {
        setRouteOfferActionError('Koncept se během ukládání výběru změnil. Trasu vyber znovu.');
        return;
      }
      changeDraft(nextDraft, true);
    } catch (error) {
      if (offer.offer_source === 'template') {
        try {
          const templates = await storage.listRouteTemplates();
          setRouteOfferData((current) => (current === null ? null : { ...current, templates }));
        } catch {
          // Keep the original CAS or storage error; a failed reload must not hide it.
        }
      }
      setRouteOfferActionError(
        errorMessage(
          error,
          offer.offer_source === 'template' && error instanceof AppEntityConflictError
            ? 'Šablona se mezitím změnila. Byla načtena její aktuální verze; vyber ji znovu.'
            : offer.offer_source === 'template'
              ? 'Zvolenou šablonu se nepodařilo bezpečně uložit a převzít.'
              : 'Zvolenou trasu se nepodařilo bezpečně převzít.',
        ),
      );
    } finally {
      routeActionBusy.current = false;
      setSelectingOfferId(null);
    }
  };

  const selectManualRoute = () => {
    if (routeActionBusy.current || favoriteActionBusy.current) return;
    try {
      setRouteOfferActionError(null);
      changeDraft(selectManualDraftRoute(draft, newUuid, routeMutationOptions));
    } catch (error) {
      setRouteOfferActionError(errorMessage(error, 'Ruční trasu se nepodařilo bezpečně vybrat.'));
    }
  };

  const toggleRouteFavorite = async (candidate: RouteCandidatePickerItem) => {
    if (favoriteActionBusy.current || routeActionBusy.current) return;
    const offer = allRouteOffers.find((item) => item.offer_id === candidate.id);
    if (offer === undefined) {
      setRouteOfferActionError('Oblíbenou variantu už nelze svázat s aktuální nabídkou.');
      return;
    }
    favoriteActionBusy.current = true;
    setFavoriteBusyId(candidate.id);
    setRouteOfferActionError(null);
    try {
      const saved = await storage.saveRouteVariantPreferences(
        routeOfferFavoriteMutation(offer, !offer.favorite),
      );
      setRouteOfferData((current) =>
        current === null
          ? null
          : {
              ...current,
              routeVariants: current.routeVariants.some(
                (route) => route.route_variant_id === saved.route_variant_id,
              )
                ? current.routeVariants.map((route) =>
                    route.route_variant_id === saved.route_variant_id ? saved : route,
                  )
                : [...current.routeVariants, saved],
            },
      );
    } catch (error) {
      try {
        const routeVariants = await storage.listRouteVariants({ include_deleted: true });
        setRouteOfferData((current) => (current === null ? null : { ...current, routeVariants }));
      } catch {
        // Keep the original preference error; a failed reload must not hide it.
      }
      setRouteOfferActionError(
        errorMessage(
          error,
          'Nastavení oblíbené varianty se nepodařilo uložit. Aktuální nabídka byla načtena znovu; zkus výběr zopakovat.',
        ),
      );
    } finally {
      favoriteActionBusy.current = false;
      setFavoriteBusyId(null);
    }
  };

  const addVia = (value?: DraftEndpointUpdateValue) => {
    changeDraft(
      addDraftVia(
        draft,
        value ?? {
          name: '',
          country_code: draft.destination?.country_code ?? draft.origin.country_code,
        },
        newUuid,
        routeMutationOptions,
      ),
    );
  };

  const changeVia = (index: number, value: DraftEndpointUpdateValue) => {
    changeDraft(updateDraftVia(draft, index, value, newUuid, routeMutationOptions));
  };

  const moveVia = (index: number, direction: 'down' | 'up') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    changeDraft(moveDraftVia(draft, index, targetIndex, newUuid, routeMutationOptions));
  };

  const removeVia = (index: number) => {
    changeDraft(removeDraftVia(draft, index, newUuid, routeMutationOptions));
  };

  const duplicateVia = (index: number) => {
    changeDraft(duplicateDraftVia(draft, index, newUuid, routeMutationOptions));
  };

  const confirmRoute = async () => {
    if (routeActionBusy.current || favoriteActionBusy.current) return;
    const startingDraft = draftRef.current;
    const startingGeneration = draftGeneration.current;
    routeActionBusy.current = true;
    setConfirmingRoute(true);
    setErrors([]);
    let next: ShiftEditorDraft;
    try {
      next = confirmManualRoute(startingDraft, new Date().toISOString());
      const selectedOffer =
        next.route_selection?.kind === 'offer'
          ? allRouteOffers.find((candidate) => routeOfferMatchesDraft(next, candidate))
          : undefined;
      const preservesHistoricalSelection =
        selectedOffer === undefined && matchesPersistedHistoricalRouteSelection(next, aggregate);
      if (
        next.route_selection?.kind === 'offer' &&
        selectedOffer === undefined &&
        !preservesHistoricalSelection
      ) {
        throw new Error('Vybraná nabídka trasy už není v aktuálním seznamu.');
      }
      if (
        next.route_selection?.kind === 'offer' &&
        next.route_selection.selection_method === 'automatic' &&
        routeOffers?.preselected_offer_id !== selectedOffer?.offer_id
      ) {
        throw new Error(
          'Automaticky předvolená trasa už neodpovídá aktuálnímu pořadí. Vyber trasu výslovně.',
        );
      }
      const knownRoutes = await storage.listRouteVariants({ include_deleted: true });
      if (startingGeneration !== draftGeneration.current || startingDraft !== draftRef.current) {
        throw new RouteActionGenerationError();
      }
      const known = knownRoutes.find((route) =>
        next.route_selection?.kind === 'manual'
          ? routeVariantMatchesConfirmedDraft(next, route)
          : selectedOffer !== undefined && routeVariantMatchesRouteOffer(selectedOffer, route),
      );
      if (known !== undefined) {
        if (next.route_selection?.kind === 'manual') {
          next = bindDraftToRouteVariant(next, known);
        } else {
          if (selectedOffer === undefined) throw new Error('route offer invariant failed');
          next = bindRouteOfferToKnownVariant(next, selectedOffer, known);
        }
      }
    } catch (error) {
      setErrors(
        error instanceof RouteActionGenerationError
          ? ['Koncept se během potvrzování změnil. Zkontroluj trasu a potvrď ji znovu.']
          : validationMessages(error, 'Vyplň platná místa Odkud a Kam.'),
      );
      routeActionBusy.current = false;
      setConfirmingRoute(false);
      return;
    }

    setDurableConfirmationKey(null);
    draftRef.current = next;
    draftGeneration.current += 1;
    setDraft(next);
    controller.setDraft(next);
    try {
      const record = await controller.saveNow();
      const storedDraft = parseShiftEditorDraft(record.payload);
      assertDraftMatchesAggregate(storedDraft, aggregate);
      if (confirmationKey(storedDraft) !== confirmationKey(next)) {
        throw new StoredShiftDraftPayloadError(
          'Uložený koncept nepotvrdil právě zobrazenou skutečnou trasu.',
        );
      }
      setDurableConfirmationKey(confirmationKey(next));
    } catch (error) {
      setErrors([
        errorMessage(
          error,
          'Výslovné potvrzení trasy se nepodařilo bezpečně uložit. Uložení úseku zůstává zablokované.',
        ),
      ]);
    } finally {
      routeActionBusy.current = false;
      setConfirmingRoute(false);
    }
  };

  const commit = async (saveAndNext: boolean) => {
    if (routeActionBusy.current || favoriteActionBusy.current) return;
    const currentValidation = validateShiftEditorDraftForCommit(draft);
    if (!currentValidation.valid) {
      setErrors(currentValidation.issues.map((issue) => issue.message));
      return;
    }
    if (!hasDurableConfirmation) {
      setErrors(['Výslovné potvrzení trasy ještě není bezpečně uložené v tomto zařízení.']);
      return;
    }
    setErrors([]);
    setCommitConflict(null);
    setCommitting(true);
    try {
      const record = await controller.saveNow();
      const storedDraft = parseShiftEditorDraft(record.payload);
      assertDraftMatchesAggregate(storedDraft, aggregate);
      const storedValidation = validateShiftEditorDraftForCommit(storedDraft);
      if (!storedValidation.valid) {
        throw new AlphaShiftDraftValidationError(storedValidation.issues);
      }
      const storedConfirmationKey = confirmationKey(storedDraft);
      const storedHasDurableConfirmation =
        storedConfirmationKey === null
          ? storedDraft.route_confirmation === null &&
            (storedDraft.status === 'draft' || storedDraft.status === 'cancelled')
          : storedConfirmationKey === durableConfirmationKey;
      if (!storedHasDurableConfirmation) {
        throw new StoredShiftDraftPayloadError(
          'Uložený koncept neobsahuje právě zobrazené výslovné potvrzení trasy.',
        );
      }
      const [existingRouteVariant, existingVehicleResults] = await Promise.all([
        storage.getRouteVariant(storedDraft.route_variant_id),
        Promise.all(storedDraft.vehicles.map((vehicle) => storage.getVehicle(vehicle.vehicle_id))),
      ]);
      const existingVehicles = existingVehicleResults.filter(
        (vehicle): vehicle is NonNullable<typeof vehicle> => vehicle !== null,
      );
      const mutation = buildShiftDraftBundleMutation({
        aggregate,
        draft: storedDraft,
        existingRouteVariant,
        existingVehicles,
      });
      const committed = await storage.commitShiftDraftBundle({
        draft_key: draftKeyFor(aggregate.shift.shift_id),
        mutation,
        expected_draft_version: {
          draft_id: record.draft_id,
          autosave_revision: record.autosave_revision,
        },
      });
      const nextDraft = saveAndNext
        ? createNextShiftEditorDraft({ aggregate: committed.shift_aggregate, uuid: newUuid })
        : null;
      onCommitted(committed.shift_aggregate, nextDraft, storedDraft.trip_segment_id);
    } catch (error) {
      if (error instanceof AppShiftConflictError || error instanceof AppEntityConflictError) {
        setCommitConflict(
          error instanceof AppShiftConflictError
            ? 'Směna byla mezitím změněna v jiném editoru. Nic nebylo sloučeno ani přepsáno.'
            : 'Použité místo, vozidlo nebo trasa byly mezitím změněny v jiném editoru. Nic nebylo sloučeno ani přepsáno.',
        );
      } else if (error instanceof AppDraftConflictError) {
        // The controller normally exposes this branch. Keep the commit path fail-closed too.
        setCommitConflict('Koncept byl mezitím změněn v jiném editoru. Uložení je zablokované.');
      } else {
        setErrors([errorMessage(error, 'Úsek se nepodařilo uložit.')]);
      }
      setCommitting(false);
    }
  };

  const loadStoredVersion = async () => {
    setResolutionBlocked(null);
    let loaded: Awaited<ReturnType<ShiftDraftAutosaveController['loadStoredVersion']>>;
    try {
      loaded = await controller.loadStoredVersion();
    } catch (error) {
      setResolutionBlocked(
        errorMessage(error, 'Uloženou verzi konceptu se nepodařilo bezpečně načíst.'),
      );
      return;
    }

    if (loaded === null) {
      setDurableConfirmationKey(null);
      setFailClosedBlock(
        'Uložený koncept mezitím zmizel, například po potvrzeném uložení v jiném okně. Lokální rozpracovaná hodnota nebyla přepsána. Nejdřív znovu načti uloženou směnu.',
      );
      return;
    }

    try {
      assertDraftMatchesAggregate(loaded.draft, aggregate);
      draftRef.current = loaded.draft;
      draftGeneration.current += 1;
      setDraft(loaded.draft);
      setDurableConfirmationKey(confirmationKey(loaded.draft));
      setErrors([]);
    } catch (error) {
      setDurableConfirmationKey(null);
      setFailClosedBlock(
        errorMessage(
          error,
          'Uložený koncept neodpovídá právě otevřené verzi směny. Automatické pokračování je zablokované.',
        ),
      );
    }
  };

  const overwriteStoredVersion = async () => {
    setResolutionBlocked(null);
    try {
      const record = await controller.overwriteWithLatest();
      const storedDraft = parseShiftEditorDraft(record.payload);
      assertDraftMatchesAggregate(storedDraft, aggregate);
      setDurableConfirmationKey(
        confirmationKey(storedDraft) === confirmationKey(draft)
          ? confirmationKey(storedDraft)
          : null,
      );
      setErrors([]);
    } catch (error) {
      setResolutionBlocked(
        errorMessage(error, 'Koncept se nepodařilo přepsat s aktuální kontrolní verzí.'),
      );
    }
  };

  const retryAutosave = async () => {
    try {
      const record = await controller.retry();
      const storedDraft = parseShiftEditorDraft(record.payload);
      assertDraftMatchesAggregate(storedDraft, aggregate);
      setDurableConfirmationKey(
        confirmationKey(storedDraft) === confirmationKey(draft)
          ? confirmationKey(storedDraft)
          : null,
      );
      setErrors([]);
    } catch {
      // The controller owns and exposes the durable retry error state.
    }
  };

  const leaveEditor = async () => {
    setErrors([]);
    setLeaving(true);
    try {
      await controller.saveNow();
      onCancel();
    } catch (error) {
      setErrors([
        errorMessage(
          error,
          'Před odchodem se koncept nepodařilo bezpečně uložit. Editor zůstává otevřený.',
        ),
      ]);
      setLeaving(false);
    }
  };

  const discardDraft = async () => {
    if (!confirmingDraftDiscard || interactionBlocked) {
      return;
    }
    setErrors([]);
    setDiscardingDraft(true);
    try {
      const record = await controller.saveNow();
      const target = safeDiscardTarget(record, controller.draftKey, storage);
      if (target === null) {
        throw new Error('Přesnou verzi uloženého konceptu nelze bezpečně svázat s tímto profilem.');
      }

      // saveNow drains every known edit generation; dispose then cancels the
      // debounce and lifecycle writers before the destructive repository CAS.
      controller.dispose();
      await storage.discardDraft(target.draftKey, target.expectedVersion);
      onReload();
    } catch (error) {
      setConfirmingDraftDiscard(false);
      setDiscardingDraft(false);
      setFailClosedBlock(
        error instanceof AppDraftConflictError
          ? 'Koncept mezitím změnila jiná relace. Novější koncept nebyl smazán. Editor už se starou verzí nebude zapisovat; načti uloženou směnu znovu.'
          : errorMessage(
              error,
              'Koncept se nepodařilo bezpečně zahodit. Zůstal beze změny v lokálním úložišti a editor už nebude zapisovat, dokud znovu nenačteš uloženou směnu.',
            ),
      );
    }
  };

  const performanceRecord = draft.status === 'complete' || draft.status === 'partial';
  const warningOrigin = safeEndpointSnapshot(draft.origin);
  const warningDestination =
    draft.destination === null ? null : safeEndpointSnapshot(draft.destination);
  const warnings = collectSegmentWarnings(aggregate, {
    target_trip_segment_id: draft.trip_segment_id,
    actual_start: draft.actual_start,
    actual_end: draft.actual_end,
    origin: warningOrigin,
    destination: warningDestination,
    train_or_service_number: draft.train_or_service_number.trim() || null,
    distance_metres: draft.distance_metres,
  }).map((warning) => warning.message);
  const splitOffer =
    persistedTarget !== undefined &&
    (persistedTarget.role !== draft.role ||
      JSON.stringify(persistedTarget.vehicles.map((vehicle) => vehicle.vehicle_id)) !==
        JSON.stringify(draft.vehicles.map((vehicle) => vehicle.vehicle_id)));
  const canCreateFollowingSegment =
    warningDestination !== null ||
    aggregate.segments.some(
      (segment) =>
        segment.metadata.tombstone === null &&
        segment.trip_segment_id !== draft.trip_segment_id &&
        segment.destination !== null,
    );

  const switchChangeToNewSegment = () => {
    if (persistedTarget === undefined) return;
    const copied = createCopiedShiftEditorDraft({
      aggregate,
      source: persistedTarget,
      uuid: newUuid,
    });
    const next: ShiftEditorDraft = {
      ...copied,
      operator: draft.operator,
      role: draft.role,
      train_or_service_number: draft.train_or_service_number,
      vehicles: draft.vehicles,
      work_type: draft.work_type,
    };
    setDurableConfirmationKey(null);
    changeDraft(next);
  };

  return (
    <div className="alpha-view alpha-shift-editor">
      <h2 ref={heading} tabIndex={-1}>
        Úsek směny {draft.service_date}
      </h2>
      <p>
        {loadedStoredDraft
          ? 'Pokračuješ v konceptu uloženém v tomto zařízení.'
          : 'Nový koncept se průběžně ukládá v tomto zařízení.'}
      </p>
      {failClosedBlock === null ? (
        <SaveStatus>
          {confirmingRoute ? 'Autosave potvrzení trasy probíhá…' : autosaveLabel(autosave.status)}
        </SaveStatus>
      ) : null}
      <FormErrorSummary errors={errors} />

      {failClosedBlock !== null ? (
        <div
          ref={failClosedError}
          className="error-summary editor-fail-closed"
          role="alert"
          tabIndex={-1}
        >
          <strong>Editor je po konfliktu bezpečně zablokovaný.</strong>
          <p>{failClosedBlock}</p>
          <button type="button" className="secondary-action touch-target" onClick={onReload}>
            Načíst uloženou směnu znovu
          </button>
        </div>
      ) : null}

      {failClosedBlock === null && autosave.status === 'conflict' ? (
        <div className="draft-conflict" role="alert">
          <strong>Konflikt konceptu</strong>
          <p>
            Tento koncept byl změněn v jiném okně. Vyber, kterou verzi chceš zachovat; aplikace je
            sama nesloučí.
          </p>
          {resolutionBlocked ? <p>{resolutionBlocked}</p> : null}
          <div className="form-actions">
            <button
              type="button"
              className="secondary-action touch-target"
              onClick={() => void loadStoredVersion()}
            >
              Načíst uloženou verzi
            </button>
            <button
              type="button"
              className="danger-action touch-target"
              onClick={() => void overwriteStoredVersion()}
            >
              Přepsat uloženou verzi touto
            </button>
          </div>
        </div>
      ) : null}

      {failClosedBlock === null && autosave.status === 'error' ? (
        <div className="error-summary" role="alert">
          <strong>Koncept se nepodařilo uložit.</strong>
          <p>{errorMessage(autosave.error, 'Zápis do lokálního úložiště selhal.')}</p>
          <button
            type="button"
            className="secondary-action touch-target"
            onClick={() => void retryAutosave()}
          >
            Zkusit autosave znovu
          </button>
        </div>
      ) : null}

      {failClosedBlock === null && commitConflict ? (
        <div className="error-summary" role="alert">
          <strong>Konflikt uložených dat</strong>
          <p>{commitConflict}</p>
          <button type="button" className="secondary-action touch-target" onClick={onReload}>
            Načíst uloženou směnu znovu
          </button>
        </div>
      ) : null}

      <form
        aria-label="Úsek směny"
        onSubmit={(event) => {
          event.preventDefault();
          void commit(false);
        }}
      >
        <p className="catalog-editor-note">
          Našeptávač hledá v offline katalogu CZ + DE. Vybrané místo se uloží s dobovou podobou
          názvu a kódů; vlastní místo můžeš dál napsat ručně.
        </p>

        <RecentLocationsPanel
          disabled={controlsDisabled}
          storage={storage}
          onSelect={(endpointName, value) => changeEndpoint(endpointName, value)}
        />

        <DraftEndpointField
          client={catalogClient}
          disabled={controlsDisabled}
          endpoint={draft.origin}
          label="Odkud"
          name="origin"
          required={performanceRecord}
          onManualChange={(value) => changeEndpoint('origin', value)}
          onSelect={(document) => changeEndpoint('origin', catalogEndpointValue(document))}
        />

        <div className="inline-actions">
          <button
            type="button"
            className="secondary-action touch-target"
            disabled={controlsDisabled || draft.destination === null}
            onClick={() => {
              if (draft.destination === null) return;
              changeDraft(reverseDraftDirection(draft, newUuid));
            }}
          >
            Prohodit Odkud a Kam
          </button>
        </div>

        <DraftEndpointField
          ref={destinationInput}
          client={catalogClient}
          disabled={controlsDisabled}
          endpoint={draft.destination}
          label="Kam"
          name="destination"
          required={performanceRecord}
          onManualChange={(value) => changeEndpoint('destination', value)}
          onSelect={(document) => changeEndpoint('destination', catalogEndpointValue(document))}
        />

        <section className="manual-route" aria-label="Skutečná trasa">
          {routeOfferLoadError ? <p role="alert">{routeOfferLoadError}</p> : null}
          {routeOfferActionError ? <p role="alert">{routeOfferActionError}</p> : null}
          {routeOfferData === null && routeOfferLoadError === null ? (
            <p role="status">Načítám dříve použité trasy a šablony…</p>
          ) : null}
          {selectingOfferId === null ? null : <p role="status">Ukládám výslovný výběr trasy…</p>}
          <RouteCandidatePicker
            candidates={pickerCandidates}
            disabled={controlsDisabled}
            favoriteBusyId={favoriteBusyId}
            manualConfirmed={manualRouteSelected(draft) && draft.route_confirmation !== null}
            manualSelected={selectingOfferId === null && manualRouteSelected(draft)}
            networkAvailability={pickerNetworkAvailability(
              routeOffers?.network_availability ?? networkAvailability,
            )}
            selectedCandidateId={selectingOfferId ?? selectedRouteOffer?.offer_id ?? null}
            onSelect={(offerId) => void selectRouteOffer(offerId)}
            onSelectManual={selectManualRoute}
            onToggleFavorite={(candidate) => void toggleRouteFavorite(candidate)}
          />
          {manualRouteSelected(draft) ? (
            <ViaPointsEditor
              client={catalogClient}
              disabled={controlsDisabled}
              points={draft.via}
              onAdd={addVia}
              onChange={changeVia}
              onDuplicate={duplicateVia}
              onMove={moveVia}
              onRemove={removeVia}
            />
          ) : null}
          <p>{routeCandidateSummary(draft)}</p>
          <button
            type="button"
            className="secondary-action touch-target"
            disabled={
              controlsDisabled ||
              draft.route_selection === null ||
              (draft.route_selection.kind === 'offer' && routeOffers === null)
            }
            onClick={() => void confirmRoute()}
          >
            {confirmingRoute
              ? 'Ukládám potvrzení trasy…'
              : draft.route_confirmation === null
                ? 'Potvrdit skutečnou trasu'
                : 'Znovu potvrdit skutečnou trasu'}
          </button>
          <p className="route-confirmation" role="status" aria-live="polite">
            {routeConfirmationLabel({
              draft,
              confirming: confirmingRoute,
              durable: hasDurableConfirmation,
              failClosed: failClosedBlock !== null,
            })}
          </p>
        </section>

        {splitOffer ? (
          <aside className="draft-conflict" role="status">
            <strong>Změnila se role nebo sestava vozidel.</strong>
            <p>
              Pokud změna nastala až během výkonu, zapiš ji jako nový úsek a uprav jeho skutečné
              místo a časy. Původní uložený úsek zůstane beze změny.
            </p>
            <button
              type="button"
              className="secondary-action touch-target"
              disabled={controlsDisabled}
              onClick={switchChangeToNewSegment}
            >
              Zapsat změnu jako nový úsek
            </button>
          </aside>
        ) : null}

        <details className="segment-details-expander">
          <summary className="secondary-action touch-target">Další údaje úseku</summary>
          <SegmentAdditionalFields
            disabled={controlsDisabled}
            draft={draft}
            expandedNonPerformanceUnavailable={expandedNonPerformanceUnavailable}
            warnings={warnings}
            onChange={changeDraft}
            plannedDestinationField={
              draft.status === 'partial' ? (
                <div className="planned-destination-field">
                  <DraftEndpointField
                    client={catalogClient}
                    disabled={controlsDisabled}
                    endpoint={draft.planned_destination}
                    label="Původně plánováno do"
                    name="planned-destination"
                    required
                    onManualChange={(value) => changeEndpoint('planned_destination', value)}
                    onSelect={(document) =>
                      changeEndpoint('planned_destination', catalogEndpointValue(document))
                    }
                  />
                  {draft.planned_destination === null ? null : (
                    <button
                      type="button"
                      className="secondary-action touch-target"
                      disabled={controlsDisabled}
                      onClick={() =>
                        changeDraft({
                          ...draft,
                          planned_destination: null,
                        })
                      }
                    >
                      Odebrat původně plánovaný cíl
                    </button>
                  )}
                </div>
              ) : null
            }
          />
        </details>

        <div className="form-actions">
          <button type="submit" className="primary-action touch-target" disabled={commitDisabled}>
            {committing ? 'Ukládám úsek…' : 'Uložit úsek'}
          </button>
          <button
            type="button"
            className="primary-action touch-target"
            disabled={commitDisabled || !canCreateFollowingSegment}
            onClick={(event) => {
              if (event.currentTarget.form?.reportValidity() === false) return;
              void commit(true);
            }}
          >
            Uložit a přidat další úsek
          </button>
          <button
            type="button"
            className="secondary-action touch-target"
            disabled={controlsDisabled}
            onClick={() => void leaveEditor()}
          >
            {leaving ? 'Ukládám koncept…' : 'Uložit koncept a zpět na směnu'}
          </button>
          {!confirmingDraftDiscard ? (
            <button
              type="button"
              className="danger-action touch-target"
              disabled={interactionBlocked}
              onClick={() => {
                setErrors([]);
                setConfirmingDraftDiscard(true);
              }}
            >
              Zahodit koncept
            </button>
          ) : null}
        </div>
        {confirmingDraftDiscard ? (
          <div ref={discardConfirmation} className="draft-conflict" role="alert" tabIndex={-1}>
            <strong>Opravdu nevratně zahodit tento koncept?</strong>
            <p>
              Autosave se nejdřív bezpečně dokončí a zastaví. Potom se přesnou kontrolní verzí smaže
              pouze tento rozpracovaný koncept; uložená směna a její uložené úseky zůstanou.
            </p>
            <div className="form-actions">
              <button
                type="button"
                className="danger-action touch-target"
                disabled={discardingDraft}
                onClick={() => void discardDraft()}
              >
                {discardingDraft ? 'Zahazuji koncept…' : 'Ano, nevratně zahodit koncept'}
              </button>
              <button
                type="button"
                className="secondary-action touch-target"
                disabled={discardingDraft}
                onClick={() => setConfirmingDraftDiscard(false)}
              >
                Nezahazovat koncept
              </button>
            </div>
          </div>
        ) : null}
        {!validation.valid || !hasDurableConfirmation || expandedNonPerformanceBlocked ? (
          <p className="field-description">
            {expandedNonPerformanceBlocked
              ? 'Rozpracovaný nebo zrušený úsek s rozepsaným místem či rozšířenou trasou teď nelze bezpečně uložit. Zvol dokončený nebo předčasně ukončený stav.'
              : validation.valid
                ? 'Uložení se zpřístupní až po bezpečném uložení výslovného potvrzení trasy.'
                : 'Uložení se zpřístupní po doplnění všech povinných údajů pro zvolený stav úseku.'}
          </p>
        ) : null}
      </form>
    </div>
  );
}

function HistoryView({
  storage,
  dataRevision,
  onOpenShift,
}: {
  readonly storage: AppStorage;
  readonly dataRevision: number;
  readonly onOpenShift: (shiftId: string) => void;
}) {
  const heading = useHeadingFocus();
  const [shifts, setShifts] = useState<readonly ShiftAggregate[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void storage.listShifts().then(
      (loaded) => {
        if (active) {
          setShifts(loaded);
        }
      },
      (error: unknown) => {
        if (active) {
          setLoadError(errorMessage(error, 'Historii se nepodařilo načíst.'));
        }
      },
    );
    return () => {
      active = false;
    };
  }, [dataRevision, storage]);

  return (
    <div className="alpha-view alpha-history">
      <div className="screen-heading">
        <p className="eyebrow">Uložené záznamy</p>
        <h2 ref={heading} tabIndex={-1}>
          Historie
        </h2>
        <p>Přehled místně uložených směn a úseků včetně bezpečné editace trasy.</p>
      </div>
      {loadError ? <p role="alert">{loadError}</p> : null}
      {shifts === null && loadError === null ? <p role="status">Načítám historii…</p> : null}
      {shifts?.length === 0 ? (
        <div className="empty-state">
          <strong>Žádné uložené směny</strong>
          <p>Zatím není uložená žádná směna.</p>
        </div>
      ) : null}
      {shifts && shifts.length > 0 ? (
        <ul className="shift-list">
          {shifts.map((aggregate) => (
            <li key={aggregate.shift.shift_id}>
              <article className="shift-list-item">
                <h3>Směna {aggregate.shift.service_date}</h3>
                <p>{segmentCount(aggregate)}</p>
                <button
                  type="button"
                  className="secondary-action touch-target"
                  onClick={() => onOpenShift(aggregate.shift.shift_id)}
                >
                  Otevřít uloženou směnu {aggregate.shift.service_date}
                </button>
              </article>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ShiftDetailView({
  dataRevision,
  storage,
  shiftId,
  successMessage,
  onBack,
  onOpenEditor,
}: {
  readonly dataRevision: number;
  readonly storage: AppStorage;
  readonly shiftId: string;
  readonly successMessage: string | null;
  readonly onBack: () => void;
  readonly onOpenEditor: (
    initialDraft: ShiftEditorDraft | null,
    focusDestination: boolean,
    requestedTripSegmentId: string | null,
  ) => void;
}) {
  const heading = useHeadingFocus();
  const [aggregate, setAggregate] = useState<ShiftAggregate | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState(successMessage);
  const [templateSource, setTemplateSource] = useState<ShiftAggregate['segments'][number] | null>(
    null,
  );
  const templateApplyBusy = useRef(false);

  useEffect(() => {
    let active = true;
    void storage.getShift(shiftId).then(
      (loaded) => {
        if (!active) {
          return;
        }
        if (loaded === null) {
          setLoadError('Uložená směna už v lokálním profilu neexistuje.');
          return;
        }
        setAggregate(loaded);
      },
      (error: unknown) => {
        if (active) {
          setLoadError(errorMessage(error, 'Směnu se nepodařilo načíst.'));
        }
      },
    );
    return () => {
      active = false;
    };
  }, [shiftId, storage]);

  const applyTemplate = async (template: RouteTemplate): Promise<boolean> => {
    if (templateApplyBusy.current) return false;
    if (aggregate === null || aggregate.shift.status !== 'open') {
      setActionMessage('Před použitím šablony směnu výslovně znovu otevři.');
      return false;
    }
    templateApplyBusy.current = true;
    try {
      const draftKey = draftKeyFor(aggregate.shift.shift_id);
      const existingDraft = await storage.getDraft(draftKey);
      if (existingDraft !== null) {
        setActionMessage(
          'Šablona nebyla započítána ani použita, protože směna už má rozpracovaný koncept nového úseku. Nejdřív tento koncept dokonči nebo výslovně zahoď.',
        );
        return false;
      }
      if (template.source_snapshot === undefined) {
        setActionMessage('Šablona nemá použitelnou uloženou podobu trasy a nebyla započítána.');
        return false;
      }
      const draft = createTemplateShiftEditorDraft({ aggregate, template, uuid: newUuid });
      const applied = await storage.applyRouteTemplateToDraft({
        draft: {
          draft_key: draftKey,
          draft_kind: 'shift_editor',
          payload: draft,
          expected_draft_version: null,
        },
        template: {
          route_template_id: template.route_template_id,
          expected_revision: template.metadata.revision,
        },
      });
      if (applied.route_template.use_count !== template.use_count + 1) {
        throw new Error('Atomické použití šablony nevrátilo očekávaný čítač použití.');
      }
      onOpenEditor(parseShiftEditorDraft(applied.draft.payload), true, null);
      return true;
    } catch (error) {
      setActionMessage(
        errorMessage(
          error,
          'Šablonu se nepodařilo atomicky uložit do konceptu a započítat, proto nebyla použita.',
        ),
      );
      return false;
    } finally {
      templateApplyBusy.current = false;
    }
  };
  return (
    <div className="alpha-view alpha-shift-detail">
      <h2 ref={heading} tabIndex={-1}>
        Uložená směna
      </h2>
      {actionMessage ? <p role="status">{actionMessage}</p> : null}
      {loadError ? <p role="alert">{loadError}</p> : null}
      {aggregate === null && loadError === null ? <p role="status">Načítám směnu…</p> : null}
      {aggregate ? (
        <>
          <dl className="shift-summary">
            <div>
              <dt>Datum služby</dt>
              <dd>{aggregate.shift.service_date}</dd>
            </div>
            <div>
              <dt>Stav</dt>
              <dd>{aggregate.shift.status === 'open' ? 'Otevřená' : 'Uzavřená'}</dd>
            </div>
            <div>
              <dt>Úseky</dt>
              <dd>{liveShiftSegments(aggregate).length}</dd>
            </div>
            <div>
              <dt>Skutečný začátek</dt>
              <dd>{storedMomentLabel(aggregate.shift.actual_start)}</dd>
            </div>
            <div>
              <dt>Skutečný konec</dt>
              <dd>{storedMomentLabel(aggregate.shift.actual_end)}</dd>
            </div>
            <div>
              <dt>Pracoviště / depo</dt>
              <dd>{aggregate.shift.home_base ?? 'neuvedeno'}</dd>
            </div>
            <div>
              <dt>Druh směny</dt>
              <dd>{aggregate.shift.shift_type ?? 'neuvedeno'}</dd>
            </div>
          </dl>
          {aggregate.shift.note === '' ? null : (
            <p>
              <strong>Poznámka ke směně:</strong> {aggregate.shift.note}
            </p>
          )}
          <h3>Uložené úseky</h3>
          {liveShiftSegments(aggregate).length === 0 ? (
            <p>Směna zatím nemá živý uložený úsek.</p>
          ) : null}
          <ol className="segment-list">
            {liveShiftSegments(aggregate).map((segment) => (
              <li key={segment.trip_segment_id}>
                <SavedSegmentCard
                  editingDisabled={aggregate.shift.status === 'closed'}
                  segment={segment}
                  onEdit={() =>
                    onOpenEditor(
                      createShiftEditorDraft({
                        aggregate,
                        targetTripSegmentId: segment.trip_segment_id,
                        uuid: newUuid,
                      }),
                      false,
                      segment.trip_segment_id,
                    )
                  }
                  onCopy={() =>
                    onOpenEditor(
                      createCopiedShiftEditorDraft({ aggregate, source: segment, uuid: newUuid }),
                      true,
                      null,
                    )
                  }
                  onReverse={() =>
                    onOpenEditor(
                      createReversedShiftEditorDraft({
                        aggregate,
                        source: segment,
                        uuid: newUuid,
                      }),
                      true,
                      null,
                    )
                  }
                  onSaveTemplate={() => setTemplateSource(segment)}
                />
              </li>
            ))}
          </ol>
          <RouteTemplatesPanel
            dataRevision={dataRevision}
            disabled={aggregate.shift.status === 'closed'}
            sourceSegment={templateSource}
            storage={storage}
            onApply={applyTemplate}
            onCancelSave={() => setTemplateSource(null)}
          />
          <ShiftCloseForm
            aggregate={aggregate}
            storage={storage}
            onSaved={(saved, message) => {
              setAggregate(saved);
              setActionMessage(message);
            }}
          />
          <div className="form-actions">
            <button
              type="button"
              className="primary-action touch-target"
              disabled={aggregate.shift.status === 'closed'}
              onClick={() =>
                onOpenEditor(
                  liveShiftSegments(aggregate).some((segment) => segment.destination !== null)
                    ? createNextShiftEditorDraft({ aggregate, uuid: newUuid })
                    : null,
                  liveShiftSegments(aggregate).some((segment) => segment.destination !== null),
                  null,
                )
              }
            >
              Přidat další úsek
            </button>
            <button type="button" className="secondary-action touch-target" onClick={onBack}>
              Zpět do historie
            </button>
          </div>
          {aggregate.shift.status === 'closed' ? (
            <p className="field-description">
              Do uzavřené směny nelze přidat úsek. Nejdřív ji výslovně znovu otevři.
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function useHeadingFocus(trigger?: unknown): RefObject<HTMLHeadingElement | null> {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus();
  }, [trigger]);
  return heading;
}

function assertDraftMatchesAggregate(draft: ShiftEditorDraft, aggregate: ShiftAggregate): void {
  if (draft.shift_id !== aggregate.shift.shift_id) {
    throw new StoredShiftDraftPayloadError('Uložený koncept patří jiné směně.');
  }
  if (draft.base_aggregate_revision !== aggregate.aggregate_revision) {
    throw new StoredShiftDraftPayloadError(
      'Uložený koncept vychází ze starší verze směny a nelze ho automaticky sloučit.',
    );
  }
  if (draft.service_date !== aggregate.shift.service_date) {
    throw new StoredShiftDraftPayloadError('Datum konceptu neodpovídá uložené směně.');
  }
}

function assertDraftMatchesEditorRequest(
  draft: ShiftEditorDraft,
  aggregate: ShiftAggregate,
  requestedTripSegmentId: string | null,
): void {
  const draftTargetsPersistedSegment = aggregate.segments.some(
    (segment) => segment.trip_segment_id === draft.trip_segment_id,
  );
  if (requestedTripSegmentId === null) {
    if (draftTargetsPersistedSegment) {
      throw new StoredShiftDraftPayloadError(
        'Uložený koncept upravuje jiný existující úsek. Přidání nového úseku ho nepřepíše.',
      );
    }
    return;
  }
  if (draft.trip_segment_id !== requestedTripSegmentId) {
    throw new StoredShiftDraftPayloadError(
      'Uložený koncept patří jinému úseku. Vybraný úsek ho nepřepíše.',
    );
  }
}

function assertDraftMatchesInitialRequest(
  storedDraft: ShiftEditorDraft,
  initialDraft: ShiftEditorDraft,
): void {
  if (canonicalJsonStringify(storedDraft) !== canonicalJsonStringify(initialDraft)) {
    throw new StoredShiftDraftPayloadError(
      'Uložený koncept neodpovídá právě otevírané variantě. Automatické pokračování je zablokované.',
    );
  }
}

function draftKeyFor(shiftId: string): string {
  return `${DRAFT_KEY_PREFIX}${shiftId}`;
}

function safeDiscardTarget(
  record: NonNullable<Awaited<ReturnType<AppStorage['getDraft']>>>,
  expectedDraftKey: string,
  storage: AppStorage,
): BlockedEditorState['discardTarget'] {
  if (
    record.draft_key !== expectedDraftKey ||
    record.local_profile_id !== storage.localProfileId ||
    record.dataset_epoch !== storage.datasetEpoch ||
    !isUuid(record.draft_id) ||
    !Number.isSafeInteger(record.autosave_revision) ||
    record.autosave_revision < 1
  ) {
    return null;
  }
  return {
    draftKey: expectedDraftKey,
    expectedVersion: {
      draft_id: record.draft_id,
      autosave_revision: record.autosave_revision,
    },
  };
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u.test(value);
}

function newUuid(): string {
  return globalThis.crypto.randomUUID();
}

function segmentCount(aggregate: ShiftAggregate): string {
  const count = aggregate.segments.filter((segment) => segment.metadata.tombstone === null).length;
  return count === 1 ? '1 úsek' : count >= 2 && count <= 4 ? `${count} úseky` : `${count} úseků`;
}

function confirmationKey(draft: ShiftEditorDraft): string | null {
  const confirmation = draft.route_confirmation;
  if (confirmation === null) {
    return null;
  }
  return JSON.stringify([
    confirmation.evidence.confirmed_at,
    confirmation.evidence.route_fingerprint_sha256,
    confirmation.selection,
    confirmation.origin.location_id,
    confirmation.origin.name,
    confirmation.origin.country_code,
    confirmation.origin.key_identifier,
    confirmation.origin.catalog_document_id,
    confirmation.origin.catalog_version,
    confirmation.via.map((endpoint) => [
      endpoint.location_id,
      endpoint.name,
      endpoint.country_code,
      endpoint.key_identifier,
      endpoint.catalog_document_id,
      endpoint.catalog_version,
    ]),
    confirmation.destination.location_id,
    confirmation.destination.name,
    confirmation.destination.country_code,
    confirmation.destination.key_identifier,
    confirmation.destination.catalog_document_id,
    confirmation.destination.catalog_version,
    confirmation.distance,
  ]);
}

function catalogEndpointValue(
  document: Parameters<typeof catalogDocumentToLocationSnapshot>[0],
): DraftEndpointUpdateValue {
  const snapshot = catalogDocumentToLocationSnapshot(document);
  return {
    name: snapshot.display_name,
    country_code: document.country,
    key_identifier: snapshot.key_identifiers[0] ?? null,
    carried_location_snapshot: snapshot,
    catalog_document_id: document.id,
    catalog_version: ACTIVE_CATALOG_VERSION,
  };
}

function safeEndpointSnapshot(endpoint: ShiftEditorDraft['origin']): LocationSnapshot | null {
  try {
    return endpointToLocationSnapshot(endpoint);
  } catch {
    return null;
  }
}

function liveShiftSegments(aggregate: ShiftAggregate): ShiftAggregate['segments'] {
  return aggregate.segments.filter((segment) => segment.metadata.tombstone === null);
}

function storedMomentLabel(moment: ShiftAggregate['shift']['actual_start']): string {
  if (moment === null) return 'neuvedeno';
  return `${moment.instant.slice(0, 16).replace('T', ' ')} · ${moment.utc_offset} · ${moment.time_zone}`;
}

function routeConfirmationLabel(input: {
  readonly draft: ShiftEditorDraft;
  readonly confirming: boolean;
  readonly durable: boolean;
  readonly failClosed: boolean;
}): string {
  if (input.failClosed) {
    return 'Stav potvrzení tohoto konceptu nelze po konfliktu považovat za aktuální.';
  }
  if (input.confirming) {
    return 'Ukládám výslovné potvrzení trasy do tohoto zařízení…';
  }
  const confirmation = input.draft.route_confirmation;
  if (confirmation === null) {
    return 'Trasa zatím není potvrzená.';
  }
  if (!input.durable) {
    return 'Výslovné potvrzení trasy ještě není bezpečně uložené.';
  }
  return `Trasa ${[
    confirmation.origin.name,
    ...confirmation.via.map((endpoint) => endpoint.name),
    confirmation.destination.name,
  ].join(' → ')} je výslovně potvrzená.`;
}

function routeOfferToPickerItem(
  offer: RouteOffer,
  group: RouteCandidatePickerItem['group'],
  confirmed: boolean,
  selectionMethod: RouteCandidatePickerItem['selectionMethod'],
): RouteCandidatePickerItem {
  return {
    autoPreselected: offer.preselected,
    confirmed,
    destinationName: offer.destination.display_name,
    distanceMetres: offer.route_seed.distance.metres,
    distanceQuality: offer.route_seed.distance.quality,
    explicitConfirmationCount: offer.explicit_confirmation_count,
    favorite: offer.favorite,
    favoriteMutable: offer.route_variant_revision !== null,
    group,
    id: offer.offer_id,
    lastUsedServiceDate: offer.last_used_service_date,
    name: offer.name,
    originName: offer.origin.display_name,
    rank: offer.score.rank,
    scorePoints: offer.score.score,
    routeVariantId: offer.route_variant_id,
    selectionMethod,
    source: offer.offer_source,
    useCount: offer.use_count,
    viaNames: offer.via.map((location) => location.display_name),
  };
}

function pickerNetworkAvailability(
  availability: NetworkRouteAvailability,
): RouteCandidatePickerNetworkAvailability {
  switch (availability.status) {
    case 'loading':
      return { status: 'loading' };
    case 'ready':
      return { status: 'ready' };
    case 'ready_no_route':
      return { status: 'ready_no_route' };
    case 'unavailable':
      return availability;
  }
}

function routeCandidateSummary(draft: ShiftEditorDraft): string {
  const seed = draft.route_seed ?? null;
  const via =
    draft.via.length === 0
      ? 'bez bodů Přes'
      : `Přes: ${draft.via.map((endpoint) => endpoint.name || 'nevyplněno').join(' → ')}`;
  const distance =
    draft.distance_input_mode === 'manual_override'
      ? draft.route_confirmation?.distance.input_mode === 'manual_override'
        ? 'ručně potvrzená kilometráž'
        : 'ručně zadaná kilometráž čekající na potvrzení'
      : draft.distance_input_mode === 'inherited' && seed !== null
        ? {
            manual: 'ručně potvrzený původ kilometráže',
            network_graph: 'kilometráž ze železničního grafu',
            trusted_import: 'kilometráž z důvěryhodného importu',
            unknown: 'kilometráž bez známé hodnoty',
          }[seed.distance.provenance]
        : 'kilometráž bez známé hodnoty';
  if (draft.route_selection === null) {
    return `Trasa zatím není vybraná; ${via}; ${distance}. Vyber dříve potvrzenou, šablonovou nebo jinou trasu.`;
  }
  if (seed === null) {
    return `Jiná trasa je sestavená z míst Odkud a Kam; ${via}; ${distance}. Nejde o navigaci ani o potvrzení aktuální průjezdnosti.`;
  }
  const source =
    draft.route_selection.kind === 'offer'
      ? {
          history: 'kopie z historie',
          network: 'návrh železničního grafu',
          template: 'uložená šablona',
        }[draft.route_selection.offer_source]
      : {
          history: 'kopie z historie',
          manual: 'dříve ručně sestavená varianta',
          network: 'návrh železničního grafu',
          template: 'uložená šablona',
        }[seed.candidate_source];
  const network =
    seed.network_segments.length === 0
      ? 'bez uložených hran grafu'
      : `${String(seed.network_segments.length)} uložených hran grafu`;
  return `Kandidát trasy: ${source}; ${via}; ${network}; ${distance}. Nejde o navigaci ani o potvrzení aktuální průjezdnosti.`;
}

function autosaveLabel(status: ReturnType<ShiftDraftAutosaveController['getSnapshot']>['status']) {
  switch (status) {
    case 'clean':
      return 'Koncept je načtený.';
    case 'dirty':
      return 'Koncept obsahuje neuložené změny.';
    case 'saving':
      return 'Ukládám koncept do tohoto zařízení…';
    case 'saved':
      return 'Koncept je uložený v tomto zařízení.';
    case 'error':
      return 'Autosave selhal. Finální uložení je zablokované.';
    case 'conflict':
      return 'Autosave je pozastavený kvůli konfliktu konceptu.';
  }
}

function validationMessages(error: unknown, fallback: string): readonly string[] {
  if (error instanceof AlphaShiftDraftValidationError) {
    return error.issues.map((issue) => issue.message);
  }
  return [errorMessage(error, fallback)];
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof AppProductionRollbackCompatibilityError) {
    return error.message;
  }
  if (error instanceof Error && error.message.trim() !== '') {
    return `${fallback} ${error.message}`;
  }
  return fallback;
}
